import React, { useMemo, useState } from 'react';
import { IconButton, MenuItem, Typography, Menu } from '@mui/material';
import Table from '../common/Table';
import { MoreHoriz } from '@mui/icons-material';
import { ConfirmModal } from '../index';
import { ConfirmModalType } from '../common/ConfirmModal';
import { GuestHelper } from '../../utils/helpers/guest.helper';

const VisitorsTable = ({
    visitors,
    onVisitorClick,
    globalFilter,
    onSuspend,
    onRemove,
    canManage
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedVisitor, setSelectedVisitor] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ open: false, action: null });

    const handleMenuClick = (event, visitor) => {
        setAnchorEl(event.currentTarget);
        setSelectedVisitor(visitor);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const openConfirmModal = (action, visitor) => {
        setSelectedVisitor(visitor);
        setConfirmModal({ open: true, action });
        handleMenuClose();
    };

    const closeConfirmModal = () => {
        setConfirmModal({ open: false, action: null });
        setSelectedVisitor(null);
    };

    const handleConfirmAction = async () => {
        if (!selectedVisitor || !confirmModal.action) return;
        closeConfirmModal();

        if (confirmModal.action === 'suspend') {
            onSuspend(selectedVisitor);
        } else if (confirmModal.action === 'delete') {
            onRemove(selectedVisitor);
        }
    };

    const columns = useMemo(() => {
        const baseColumns = [
            {
                Header: 'Name',
                accessor: row => {
                    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim();
                    const company = row.company_name || '';
                    return company ? `${name} ${company}` : name;
                },
                Cell: ({ row }) => GuestHelper.formatPerson(row.original)
            },
            {
                Header: 'Status',
                accessor: 'status',
                className: 'cell-center',
                Cell: ({ row }) => {
                    const isSuspended = row.original.suspended;
                    const label = isSuspended ? 'Suspended' : 'Active';
                    return (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '80%',
                                width: '30%',
                                backgroundColor: isSuspended
                                    ? 'rgba(255, 0, 0, .1)'
                                    : 'rgba(45, 143, 87, .1)',
                                borderRadius: '5px',
                                paddingLeft: '10px',
                                paddingRight: '10px'
                            }}
                        >
                            <Typography
                                sx={{ fontSize: '13px', marginX: '10px' }}
                                color={
                                    isSuspended
                                        ? 'rgba(255, 0, 0, 1)'
                                        : 'rgba(45, 143, 87, 1)'
                                }
                            >
                                {label}
                            </Typography>
                        </div>
                    );
                }
            }
        ];

        if (canManage) {
            baseColumns.push({
                Header: 'Actions',
                className: 'cell-right',
                Cell: ({ row }) => (
                    <IconButton
                        onClick={e => {
                            e.stopPropagation();
                            handleMenuClick(e, row.original);
                        }}
                    >
                        <MoreHoriz />
                    </IconButton>
                )
            });
        }

        return baseColumns;
    }, [canManage]);

    return (
        <>
            <Table
                columns={columns}
                data={visitors}
                pagination={true}
                onRowClick={onVisitorClick}
                externalGlobalFilter={globalFilter}
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => openConfirmModal('suspend', selectedVisitor)}>
                    {selectedVisitor?.suspended ? 'Unsuspend' : 'Suspend'}
                </MenuItem>
                <MenuItem onClick={() => openConfirmModal('delete', selectedVisitor)}>
                    Delete
                </MenuItem>
            </Menu>

            <ConfirmModal
                open={confirmModal.open}
                onClose={closeConfirmModal}
                onConfirm={handleConfirmAction}
                title={
                    confirmModal.action === 'suspend'
                        ? `Confirm ${
                              selectedVisitor?.suspended ? 'Unsuspension' : 'Suspension'
                          }`
                        : 'Confirm Deletion'
                }
                text={
                    confirmModal.action === 'suspend'
                        ? `Are you sure you want to ${
                              selectedVisitor?.suspended ? 'unsuspend' : 'suspend'
                          } this visitor?`
                        : 'Are you sure you want to delete this visitor? This action cannot be undone.'
                }
                confirmText={confirmModal.action === 'delete' ? 'Delete' : 'Confirm'}
                type={
                    confirmModal.action === 'suspend'
                        ? ConfirmModalType.WARNING
                        : ConfirmModalType.ERROR
                }
            />
        </>
    );
};

export default VisitorsTable;
